import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AiPreviewData, CampaignBuilderMode } from '@/features/campaigns/types/campaign';
import { emptyAiForm } from '@/features/campaigns/types/campaign';
import {
  clearAiCampaignDraft,
  saveAiCampaignDraft,
} from '@/features/campaigns/utils/aiCampaignDraftStorage';
import { readInitialAiState } from './campaignCreatePage.initialState';

type UseAiCampaignStateParams = {
  routeMode?: string;
  builderMode: CampaignBuilderMode | null;
  isCampaignCompleted: boolean;
  showSuccess: boolean;
  freshStart?: boolean;
};

/** Owns the AI-brief form, generated plan, and localStorage draft persistence for the AI builder flow. */
export function useAiCampaignState({
  routeMode,
  builderMode,
  isCampaignCompleted,
  showSuccess,
  freshStart = false,
}: UseAiCampaignStateParams) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialAi = useMemo(
    () => readInitialAiState(routeMode, { fresh: freshStart }),
    [],
  );

  const [formData, setFormData] = useState(initialAi.formData);
  const [aiGeneratedData, setAiGeneratedData] = useState<AiPreviewData | null>(
    initialAi.aiGeneratedData,
  );

  useEffect(() => {
    if (builderMode !== 'ai') return;
    if (isCampaignCompleted || showSuccess) {
      clearAiCampaignDraft();
      return;
    }
    saveAiCampaignDraft({ formData, aiGeneratedData });
  }, [builderMode, formData, aiGeneratedData, isCampaignCompleted, showSuccess]);

  const resetAi = useCallback(() => {
    clearAiCampaignDraft();
    setAiGeneratedData(null);
    setFormData(emptyAiForm());
  }, []);

  return {
    formData,
    setFormData,
    aiGeneratedData,
    setAiGeneratedData,
    resetAi,
  };
}
